import React, { useState, useEffect, useCallback, useContext } from 'react'; // Import useContext
import { Link } from 'react-router-dom';
import apiClient from '../api';
import { formatDate } from '../utils/formatDate';
import AuthContext from '../context/AuthContext'; // Import AuthContext
import Pagination from '../components/Pagination';

function JuvenilesListPage() {
  const [juveniles, setJuveniles] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const { hasPermission } = useContext(AuthContext); // Get the hasPermission function

  // Add state for pagination ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const fetchJuveniles = useCallback(async () => {
    setIsLoading(true);
   
    try {
      // Use apiClient, which has the correct URL and handles auth automatically. Add the 'page' parameter to the API call
      const response = await apiClient.get(`/api/juveniles?search=${appliedSearch}&page=${currentPage}`);
      setJuveniles(response.data.juveniles || []);
      // Set the total pages from the API response
      setTotalPages(Math.ceil(response.data.total / response.data.limit));
    } catch (err) {
      setError('Failed to fetch juveniles.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [appliedSearch, currentPage]); // Add currentPage as a dependency

  useEffect(() => {
    fetchJuveniles();
  }, [fetchJuveniles]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to page 1 for a new search
    setAppliedSearch(searchTerm);
  };

   // Handler to change the current page
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return <div className="card"><p>Loading juveniles...</p></div>;
  }

  if (error) {
    return <div className="card"><p className="text-red-500">{error}</p></div>;
  }

  return (
    <div className="card">
      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold text-slate-800 mb-0">Juvenile Roster</h1>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <form onSubmit={handleSearch} className="flex-grow md:flex-grow-0">
            <input
              type="text"
              placeholder="Search by Name or Case #"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border rounded-lg py-2 px-4"
            />
            <button type="submit" className="ml-2 bg-slate-200 text-slate-700 hover:bg-slate-300 font-bold py-2 px-4 rounded-lg">Search</button>
          </form>
          <Link to="/advanced-search" className="text-sm text-slate-600 hover:text-gold">Advanced</Link>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="p-4 font-semibold text-slate-600">Name</th>
              <th className="p-4 font-semibold text-slate-600">Date of Birth</th>
              <th className="p-4 font-semibold text-slate-600">Primary Officer</th>
              <th className="p-4 font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {juveniles.length > 0 ? (
              juveniles.map(juvenile => (
                <tr key={juvenile.id} className="border-b hover:bg-slate-50">
                  <td className="p-4 font-medium text-slate-800">{juvenile.first_name} {juvenile.last_name}</td>
                  <td className="p-4 text-slate-600">{formatDate(juvenile.date_of_birth)}</td>
                  <td className="p-4 text-slate-600">{juvenile.primary_officer_name || 'N/A'}</td>
                  <td className="p-4">
                    <Link to={`/juveniles/${juvenile.id}`} className="bg-gold text-white hover:bg-gold-dark font-bold py-1 px-3 rounded-lg text-sm">
                      View Profile
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="p-4 text-center text-slate-500">No juveniles found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-6 pt-4 border-t">
        {/* This empty div takes up space on the left if the user can't add juveniles, ensuring the pagination stays centered */}
        <div className="flex-1"></div>
        <div className="flex-1">
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
        {/*  Conditionally render the 'Add' button */}
        {hasPermission('create-juvenile') && (
          <Link to="/juveniles/new" className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-md">
            + Add New Juvenile
          </Link>
        )}
      </div>
    </div>
  );
}

export default JuvenilesListPage;